/**
 * Schedule Page
 *
 * Calendar view showing:
 * - Weekly and Monthly view toggle
 * - Workout history from Supabase
 * - Event markers for dates with workouts
 */

import { useState, useEffect } from 'react';
import { CalendarGrid } from '@/components/Schedule/CalendarGrid';
import { EventList } from '@/components/Schedule/EventList';
import { WorkoutViewModal } from '@/components/common/WorkoutViewModal';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import './Schedule.css';

export type CalendarView = 'weekly' | 'monthly';

interface WorkoutEvent {
  id: string;
  title: string;
  type: 'workout';
  date: Date;
  duration: string;
  sets: number;
}

export function Schedule(): JSX.Element {
  const { user } = useAuth();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [view, setView] = useState<CalendarView>('weekly');
  const [showViewDropdown, setShowViewDropdown] = useState(false);
  const [events, setEvents] = useState<WorkoutEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewWorkoutId, setViewWorkoutId] = useState<string | null>(null);

  // Fetch workout events from Supabase
  useEffect(() => {
    async function fetchWorkouts(): Promise<void> {
      if (!user) {
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('workout_logs')
        .select(`
          id,
          started_at,
          completed_at,
          status,
          notes,
          workout_log_sets (id)
        `)
        .eq('user_id', user.id)
        .eq('status', 'completed')
        .order('started_at', { ascending: false });

      if (error) {
        console.error('Failed to fetch workouts:', error);
        setLoading(false);
        return;
      }

      const workoutEvents: WorkoutEvent[] = (data ?? []).map((workout) => {
        const startedAt = new Date(workout.started_at);
        const completedAt = workout.completed_at ? new Date(workout.completed_at) : null;

        // Calculate duration
        let duration = '0m';
        if (completedAt) {
          const durationMs = completedAt.getTime() - startedAt.getTime();
          const minutes = Math.floor(durationMs / 60000);
          const hours = Math.floor(minutes / 60);
          const remainingMinutes = minutes % 60;
          duration = hours > 0 ? `${hours}h ${remainingMinutes}m` : `${minutes}m`;
        }

        const sets = workout.workout_log_sets as { id: string }[];

        return {
          id: workout.id,
          title: `${workout.notes ?? 'Workout'} Day`,
          type: 'workout' as const,
          date: startedAt,
          duration,
          sets: sets.length,
        };
      });

      setEvents(workoutEvents);
      setLoading(false);
    }

    fetchWorkouts();
  }, [user]);

  const handleTodayClick = (): void => {
    setSelectedDate(new Date());
  };

  const handleViewSelect = (newView: CalendarView): void => {
    setView(newView);
    setShowViewDropdown(false);
  };

  const todayEvents = events.filter((e) => {
    const today = new Date();
    return e.date.toDateString() === today.toDateString();
  });

  const selectedDateEvents = events.filter(
    (e) => e.date.toDateString() === selectedDate.toDateString()
  );

  const handleEventClick = (event: { id: string }): void => {
    setViewWorkoutId(event.id);
  };

  return (
    <div className="schedule">
      <div className="schedule__header">
        <h1 className="schedule__title">Schedule</h1>
        <div className="schedule__controls">
          <button
            type="button"
            className="schedule__today-button"
            onClick={handleTodayClick}
          >
            Today
          </button>
          <div className="schedule__view-dropdown">
            <button
              type="button"
              className="schedule__view-button"
              onClick={() => setShowViewDropdown(!showViewDropdown)}
            >
              {view === 'weekly' ? 'Week' : 'Month'}
              <span className="schedule__view-icon">▼</span>
            </button>
            {showViewDropdown && (
              <div className="schedule__view-menu">
                <button
                  type="button"
                  className={`schedule__view-option ${view === 'weekly' ? 'schedule__view-option--active' : ''}`}
                  onClick={() => handleViewSelect('weekly')}
                >
                  Weekly
                </button>
                <button
                  type="button"
                  className={`schedule__view-option ${view === 'monthly' ? 'schedule__view-option--active' : ''}`}
                  onClick={() => handleViewSelect('monthly')}
                >
                  Monthly
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="schedule__loading">Loading...</div>
      ) : (
        <>
          <CalendarGrid
            selectedDate={selectedDate}
            onDateSelect={setSelectedDate}
            events={events}
            view={view}
          />

          {selectedDateEvents.length > 0 && (
            <div className="schedule__section">
              <h2 className="schedule__section-title">
                {selectedDate.toDateString() === new Date().toDateString()
                  ? "Today's Workouts"
                  : `Workouts on ${selectedDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`}
              </h2>
              <EventList events={selectedDateEvents} onEventClick={handleEventClick} />
            </div>
          )}

          {todayEvents.length === 0 && selectedDate.toDateString() === new Date().toDateString() && (
            <div className="schedule__section">
              <h2 className="schedule__section-title">Today's Schedule</h2>
              <div className="schedule__empty">No workouts scheduled for today</div>
            </div>
          )}

          <div className="schedule__section">
            <h2 className="schedule__section-title">Recent Workouts</h2>
            {events.length === 0 ? (
              <div className="schedule__empty">No completed workouts yet</div>
            ) : (
              <EventList events={events.slice(0, 5)} onEventClick={handleEventClick} />
            )}
          </div>
        </>
      )}

      {/* Workout View Modal */}
      {viewWorkoutId && (
        <WorkoutViewModal
          workoutId={viewWorkoutId}
          onClose={() => setViewWorkoutId(null)}
        />
      )}
    </div>
  );
}
